import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DeployStatusPanel } from '../components/sites/DeployStatusPanel.js';
import { BuildHistoryPanel } from '../components/sites/BuildHistoryPanel.js';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  Globe,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
  GitBranch,
  FolderOpen,
  Calendar,
  Newspaper,
  Rocket,
} from 'lucide-react';
import { getEgressTarget, getTargetArticles, getTargetHistory } from '../lib/api/index.js';
import { format } from 'date-fns';

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [historyPage, setHistoryPage] = useState(1);
  const [historyStatus, setHistoryStatus] = useState<string | undefined>(undefined);
  const [articlesPage, setArticlesPage] = useState(1);

  const { data: target, isLoading, error } = useQuery({
    queryKey: ['egress-target', id],
    queryFn: () => getEgressTarget(id!),
    enabled: !!id,
  });

  const { data: articlesData } = useQuery({
    queryKey: ['target-articles', id, articlesPage],
    queryFn: () => getTargetArticles(id!, { page: articlesPage, limit: 20 }),
    enabled: !!id,
  });

  // Git-derived sync status — replaces the publishing_history-based
  // needs_sync proxy as the gate for the Promote panel. We hit GitHub's
  // compare(main...test) API via the publishing service so the count
  // reflects actual git drift.

  const { data: historyData } = useQuery({
    queryKey: ['target-history', id, historyPage, historyStatus],
    queryFn: () => getTargetHistory(id!, { page: historyPage, limit: 20, status: historyStatus }),
    enabled: !!id,
  });

  // Promote mutation. Wrapped in useMutation so the button can disable
  // itself while the request is in flight (preventing double-click) and
  // surface a 'Promoting…' affordance. Without this, a second click
  // before the first response returns races a second create-PR call,
  // which 422s with "No commits between main and test" once the first
  // PR has merged — surfacing a misleading error toast even though
  // production was already updated.

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
          <p className="mt-6 text-grey-600 font-medium">Loading site...</p>
        </div>
      </div>
    );
  }

  if (error || !target) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-16 w-16 text-danger-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-grey-900 mb-2">Site Not Found</h2>
        <Link
          to="/publishing"
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Publishing</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div>
        <Link
          to="/publishing"
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Publishing</span>
        </Link>

        <div className="flex items-center space-x-4">
          <Globe className="h-8 w-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-grey-900">{target.target_name}</h1>
          <span className="text-sm px-3 py-1 rounded-full font-medium bg-grey-100 text-grey-600">
            {target.target_type}
          </span>
          {target.is_active ? (
            <CheckCircle className="h-6 w-6 text-success-600" />
          ) : (
            <XCircle className="h-6 w-6 text-grey-400" />
          )}
        </div>
      </div>

      {/* Overview Card */}
      <div className="bg-surface rounded-xl border border-grey-200 p-6 shadow-md">
        <h2 className="text-lg font-bold text-grey-900 mb-4">Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {target.config.github_repo && (
            <div className="flex items-center space-x-2 text-grey-700">
              <GitBranch className="h-4 w-4 text-grey-500" />
              <span className="font-medium">Repo:</span>
              <span className="text-grey-600 truncate">{target.config.github_repo}</span>
            </div>
          )}
          {target.config.domain && (
            <div className="flex items-center space-x-2 text-grey-700">
              <ExternalLink className="h-4 w-4 text-grey-500" />
              <span className="font-medium">Domain:</span>
              <span className="text-grey-600">{target.config.domain}</span>
            </div>
          )}
        </div>
      </div>

      {/* Destinations */}
      <div className="bg-surface rounded-xl border border-grey-200 p-6 shadow-md">
        <h2 className="text-lg font-bold text-grey-900 mb-4">Destinations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {target.destinations.map((dest) => (
            <div key={dest.destination_id} className="border border-grey-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-grey-900">{dest.destination_name}</h3>
                <div className="flex items-center space-x-2">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-grey-100 text-grey-600">
                    {dest.destination_type}
                  </span>
                  {dest.is_active ? (
                    <CheckCircle className="h-4 w-4 text-success-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-grey-400" />
                  )}
                </div>
              </div>
              <div className="space-y-1 text-sm text-grey-600">
                {dest.config.branch && (
                  <div className="flex items-center space-x-2">
                    <GitBranch className="h-3.5 w-3.5" />
                    <span>{dest.config.branch}</span>
                  </div>
                )}
                {dest.config.domain && (
                  <div className="flex items-center space-x-2">
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>{dest.config.domain}</span>
                  </div>
                )}
              </div>
              {dest.components.length > 0 && (
                <div className="mt-3 pt-3 border-t border-grey-100">
                  <p className="text-xs font-semibold text-grey-500 mb-1">Components</p>
                  <div className="flex flex-wrap gap-1.5">
                    {dest.components.map((comp) => (
                      <span
                        key={comp.component_id}
                        className="text-xs px-2 py-0.5 rounded bg-primary-50 text-primary-700 font-medium"
                      >
                        {comp.component_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Promote to Production — git-driven, always rendered.
          Source of truth is GitHub's compare(main...test); ahead_by is
          the number of commits that a promote PR would carry. We fall
          back to publishing_history needs_sync only if the sync-status
          call failed (e.g. publishing service down). The site-level
          promote is wholesale: test → main PR shipping every drifted
          commit. Article-level snapshot promote lives on the article
          detail page. */}
      {/* Every masthead builds server-side (migration 323 retired the last
          cf_workers_builds target), so this is no longer a choice. The old
          arm rendered a git-drift panel and the WHOLESALE promote button —
          "is test ahead of main", answered by a GitHub compare(main...test).
          That was the right question when a git push triggered a Cloudflare
          build. It has not been since 2026-08-26: a merge to main builds
          nothing, and what makes a page live is our own build+deploy.

          Deleted rather than left behind a dead `deploy_mode` check: an arm
          nothing can reach still costs a sync-status fetch on every page
          load, and still reads as a supported mode to whoever finds it next.

          The wholesale promote button went with it — D2, retire the verb.
          It was already unreachable; this makes that explicit. */}
      <DeployStatusPanel
        targetId={id!}
        publishDomain={(target.config as any)?.publish_domain}
      />

      {/* The history behind the current state. Step 1 surfaced the build that
          is failing NOW; this is every attempt, including what each one said.
          publication_build_runs has held it since migration 317. */}
      <div className="mt-4">
        <BuildHistoryPanel targetId={id!} />
      </div>

      {/* Published Articles */}
      <div className="bg-surface rounded-xl border border-grey-200 p-6 shadow-md">
        <h2 className="text-lg font-bold text-grey-900 mb-4">Published Articles</h2>
        {articlesData && articlesData.items.length > 0 ? (
          <>
            <div className="space-y-3">
              {articlesData.items.map((article) => (
                <div
                  key={article.article_id}
                  className="flex items-center justify-between p-4 border border-grey-200 rounded-lg hover:bg-grey-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/articles/${article.article_id}`}
                        className="font-semibold text-primary-700 hover:text-primary-800 truncate"
                      >
                        {article.article_title}
                      </Link>
                      {article.needs_sync && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-warning-100 text-warning-700 border border-warning-200 flex-shrink-0">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Needs Sync
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-grey-500">
                      {article.destination_name && <span>{article.destination_name}</span>}
                      <span>{article.word_count} words</span>
                      {article.last_published_at && (
                        <span className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{format(new Date(article.last_published_at), 'MMM d, yyyy HH:mm')}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 ml-4">
                    {article.external_url && (
                      <a
                        href={article.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-grey-400 hover:text-primary-600 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                        article.publish_status === 'published'
                          ? 'bg-success-100 text-success-700'
                          : article.publish_status === 'approved'
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-grey-100 text-grey-600'
                      }`}
                    >
                      {article.publish_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {articlesData.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-grey-200">
                <span className="text-sm text-grey-600">Page {articlesPage} of {articlesData.pages}</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setArticlesPage(p => Math.max(1, p - 1))}
                    disabled={articlesPage === 1}
                    className="px-3 py-1.5 border border-grey-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setArticlesPage(p => p + 1)}
                    disabled={articlesPage >= articlesData.pages}
                    className="px-3 py-1.5 border border-grey-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <Newspaper className="h-10 w-10 text-grey-300 mx-auto mb-2" />
            <p className="text-grey-500">No articles published to this site yet.</p>
          </div>
        )}
      </div>

      {/* Publishing Activity */}
      <div className="bg-surface rounded-xl border border-grey-200 p-6 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-grey-900">Activity</h2>
          <div className="flex space-x-2">
            {['all', 'success', 'failed', 'unpublished'].map((s) => (
              <button
                key={s}
                onClick={() => {
                  setHistoryStatus(s === 'all' ? undefined : s);
                  setHistoryPage(1);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  (s === 'all' && !historyStatus) || historyStatus === s
                    ? 'bg-primary-100 text-primary-700 border border-primary-200'
                    : 'text-grey-600 hover:bg-grey-100'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {historyData && historyData.items.length > 0 ? (
          <>
            <div className="space-y-3">
              {historyData.items.map((entry) => (
                <div
                  key={entry.publish_id}
                  className="flex items-start space-x-3 p-3 border border-grey-100 rounded-lg"
                >
                  <div className="mt-0.5">
                    {entry.status === 'success' ? (
                      <CheckCircle className="h-5 w-5 text-success-600" />
                    ) : entry.status === 'failed' ? (
                      <XCircle className="h-5 w-5 text-danger-600" />
                    ) : entry.status === 'unpublished' ? (
                      <AlertCircle className="h-5 w-5 text-grey-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-grey-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/articles/${entry.article_id}`}
                        className="font-medium text-grey-900 hover:text-primary-700 truncate"
                      >
                        {entry.article_title}
                      </Link>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          entry.status === 'success'
                            ? 'bg-success-100 text-success-700'
                            : entry.status === 'failed'
                            ? 'bg-danger-100 text-danger-700'
                            : entry.status === 'unpublished'
                            ? 'bg-grey-100 text-grey-600'
                            : 'bg-grey-100 text-grey-600'
                        }`}
                      >
                        {entry.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-grey-500">
                      {entry.destination_name && <span>{entry.destination_name}</span>}
                      {entry.published_at && (
                        <span>{format(new Date(entry.published_at), 'MMM d, yyyy HH:mm')}</span>
                      )}
                    </div>
                    {entry.external_url && (
                      <a
                        href={entry.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 mt-1 text-xs text-primary-600 hover:text-primary-700"
                      >
                        <ExternalLink className="h-3 w-3" />
                        <span>View</span>
                      </a>
                    )}
                    {entry.error_message && (
                      <p className="mt-1 text-xs text-danger-600">{entry.error_message}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {historyData.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-grey-200">
                <span className="text-sm text-grey-600">Page {historyPage} of {historyData.pages}</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                    disabled={historyPage === 1}
                    className="px-3 py-1.5 border border-grey-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setHistoryPage(p => p + 1)}
                    disabled={historyPage >= historyData.pages}
                    className="px-3 py-1.5 border border-grey-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="h-10 w-10 text-grey-300 mx-auto mb-2" />
            <p className="text-grey-500">No publishing activity yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
